import { Fragment, useMemo } from 'react';
import { BarRounded } from '@visx/shape';
import { Group } from '@visx/group';
import { scaleBand, scaleLinear } from '@visx/scale';
import { ParentSize } from '@visx/responsive';
import { useTooltip } from '@visx/tooltip';
import { GridRows } from '@visx/grid';
import { AxisLeft } from '@visx/axis';
import { localPoint } from '@visx/event';
import classNames from 'classnames';

import { NoMatchBanner } from '@/components/banner';
import { formatPropertyValue } from '@/explore/utils/format';
import { useExplorationCellContext } from '@/explore/exploration/exploration-cell-context';
import { Field, FilterOperation } from '@/explore/types';
import { DropdownMenuItem } from '@/components/dropdown';
import { InteractableProps } from '@/explore/utils/interactable';

import { Label, TooltipData, ChartTooltipSingleValue } from '../common';
import { SeriesMetaData } from '../grouped-chart/types';
import { getScreenPoint, increaseBrightness } from '../utils';
import { Legend } from '../legend';

import commonStyles, { gridLineColor } from '../charts.module.scss';
import styles from './histogram-chart.module.scss';

export interface HistogramDataItem {
  bucket_low: number;
  bucket_high: number;
  freq: number;
  bucket: number;
}

const horizontalPadding = 25;
const verticalPadding = 10;
const yAxisLabelOffset = 20;
const paddingAroundLabel = 10;
const textHeight = 20;
const numTicks = 5;
const legendHeight = 20;

const defaultHeight = 100;

const formatLabel = ({
  bucketHigh,
  bucketLow,
  field,
}: {
  bucketHigh: number | null;
  bucketLow: number | null;
  field: Field;
}) => {
  if (bucketLow === null || bucketHigh === null) {
    return null;
  }

  return bucketLow === bucketHigh
    ? formatPropertyValue(bucketLow, { field, style: 'compact' })
    : `[${formatPropertyValue(bucketLow, { field, style: 'compact' })} ... ${formatPropertyValue(bucketHigh, { field, style: 'compact' })})`;
};

type HistogramVisProps = {
  data: {
    data: HistogramDataItem[];
    series: SeriesMetaData[];
  };
  categoryLabel: string;
  field: Field;
  onBucketClick?: (props: { x: number; y: number; filters: FilterOperation[] }) => void;
  buildSeriesMenu: (seriesKey: string) => DropdownMenuItem[];
};

const Chart = ({
  data,
  width,
  height = defaultHeight,
  categoryLabel,
  field,
  onBucketClick,
  buildSeriesMenu,
}: HistogramVisProps & { width: number; height: number }) => {
  const { showTooltip, hideTooltip, tooltipOpen, tooltipLeft, tooltipTop, tooltipData } =
    useTooltip<TooltipData>();

  const handleMouseOver = (event: any, data: TooltipData) => {
    const point = localPoint(event);
    if (point !== null) {
      const screenPoint = getScreenPoint(point, event.target);
      showTooltip({ tooltipLeft: screenPoint.x, tooltipTop: screenPoint.y, tooltipData: data });
    }
  };

  // do not show null bucket if it's empty
  const filteredData = useMemo(
    () => data.data.filter(({ bucket_low, freq }) => bucket_low !== null || freq > 0),
    [data],
  );

  const chartHeight = height - legendHeight;
  const xMax = width - horizontalPadding * 2 - yAxisLabelOffset;
  const yMax = chartHeight - verticalPadding * 2 - paddingAroundLabel * 2 - textHeight;
  const maxFreq = Math.max(...filteredData.map((d) => d.freq));

  const xScale = useMemo(
    () =>
      scaleBand<number>({
        range: [0, xMax],
        round: true,
        domain: filteredData.map(({ bucket }) => bucket),
      }),
    [xMax, filteredData],
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        range: [yMax, 0],
        round: true,
        nice: true,
        domain: [0, maxFreq],
      }),
    [yMax, maxFreq],
  );

  if (filteredData.length === 0) {
    return <NoMatchBanner />;
  }

  const series = data.series[0];

  return (
    <div className={styles.histogramChart}>
      <ChartTooltipSingleValue
        position={{ x: tooltipLeft ?? 0, y: tooltipTop ?? 0 }}
        tooltipData={tooltipData}
        tooltipOpen={tooltipOpen}
        categoryLabel={categoryLabel}
        valueLabel={series.label}
        seriesColor={series.color}
      />
      <svg width={width} height={chartHeight}>
        <GridRows
          top={verticalPadding * 2}
          width={width}
          scale={yScale}
          numTicks={numTicks}
          height={yMax}
          stroke={gridLineColor}
        />
        <AxisLeft
          left={horizontalPadding + yAxisLabelOffset}
          top={verticalPadding * 2}
          scale={yScale}
          numTicks={numTicks}
          hideTicks
          hideAxisLine
          hideZero
          tickClassName={commonStyles.tickLabel}
          tickLabelProps={() => ({
            dy: 10,
            textAnchor: 'end',
          })}
          tickFormat={(n) => formatPropertyValue(n.valueOf(), { type: 'Number', style: 'compact' })}
        />
        <Group left={horizontalPadding + yAxisLabelOffset}>
          {filteredData.map(
            ({ bucket_low: bucketLow, bucket_high: bucketHigh, freq, bucket }, idx) => {
              const label = formatLabel({ bucketLow, bucketHigh, field });
              const value = freq;

              const barWidth = xScale.bandwidth();
              // if there is a value but it's too small to show, show a 2px bar at a minimum
              const barSize = value === null || value === 0 ? 0 : Math.max(yMax - yScale(value), 2);

              const barX = xScale(bucket) ?? 0;
              const barY = yMax - barSize + verticalPadding * 2;

              const isLast = idx === filteredData.length - 1;

              const gap = xScale.step() - xScale.bandwidth();

              return (
                <Fragment key={bucket}>
                  {barSize > 0 && (
                    <>
                      <BarRounded
                        {...InteractableProps}
                        x={barX}
                        y={barY}
                        width={barWidth - 4}
                        height={barSize}
                        fill={series.color ?? 'url(#grad-brand)'}
                        onMouseMove={(e) =>
                          handleMouseOver(e, {
                            label,
                            value,
                            type: 'Integer',
                          })
                        }
                        onMouseOut={hideTooltip}
                        radius={5}
                        top
                        onClick={(e) => {
                          const { clientX, clientY } = e;
                          const range = {
                            start: bucketLow,
                            end: bucketHigh,
                          };
                          onBucketClick?.({
                            x: clientX,
                            y: clientY,
                            filters: [
                              {
                                operation: 'filter',
                                parameters: {
                                  operator: 'and',
                                  operands: [
                                    {
                                      key: field.key,
                                      operator: '>=',
                                      value: range.start,
                                    },

                                    {
                                      key: field.key,
                                      operator: '<',
                                      value: range.end,
                                    },
                                  ],
                                },
                              },
                            ],
                          });
                        }}
                      />
                      {series.showValues && (
                        <text
                          className={styles.valueLabel}
                          x={barX + barWidth / 2}
                          y={barY}
                          dx={-4}
                          dy={-10}
                          fill={increaseBrightness(series.color, 20)}>
                          {formatPropertyValue(value, {
                            type: series.type,
                            format: series.format,
                            style: 'compact',
                          })}
                        </text>
                      )}
                    </>
                  )}
                  <Label
                    verticalAnchor="middle"
                    x={barX - gap / 2}
                    textAnchor="middle"
                    y={chartHeight - paddingAroundLabel - textHeight / 2}
                    hideTooltip={hideTooltip}
                    label={formatPropertyValue(bucketLow, {
                      field,
                      style: 'compact',
                    })}
                    value={value}
                    width={barWidth}
                  />
                  {isLast && (
                    <Label
                      verticalAnchor="middle"
                      x={barX + barWidth + gap / 2}
                      textAnchor="middle"
                      y={chartHeight - paddingAroundLabel - textHeight / 2}
                      hideTooltip={hideTooltip}
                      label={formatPropertyValue(bucketHigh, {
                        field,
                        style: 'compact',
                      })}
                      value={value}
                      width={barWidth}
                    />
                  )}
                </Fragment>
              );
            },
          )}
        </Group>
      </svg>
      <div
        style={{
          paddingLeft: horizontalPadding + yAxisLabelOffset,
          paddingRight: horizontalPadding,
        }}>
        <Legend
          data={{
            type: 'basic',
            items: data.series,
          }}
          buildSeriesMenu={buildSeriesMenu}
        />
      </div>
    </div>
  );
};

export const Histogram = (
  props: HistogramVisProps & {
    isResized?: boolean;
  },
) => {
  const { isTableVisible } = useExplorationCellContext();

  return (
    <ParentSize
      debounceTime={0}
      className={classNames(commonStyles.chartContainer, {
        [commonStyles.customHeight]: !isTableVisible && props.isResized,
      })}>
      {(parent) => <Chart {...props} width={parent.width} height={parent.height} />}
    </ParentSize>
  );
};
